import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from './context/ThemeContext';
import { fetchLocationsFilter } from './data/staticData';

/**
 * Filter dropdown component matching reference design
 */
const FilterDropdown = ({ label, options, value, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <div className="filter-dropdown" ref={dropdownRef} onClick={() => setIsOpen(!isOpen)}>
            <button
                className="filter-dropdown-button"
            >
                <span className="filter-label">{label}</span>
                {/* <svg
                    className={`filter-arrow ${isOpen ? 'open' : ''}`}
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <path d="M6 9l6 6 6-6" />
                </svg> */}
            </button>
            {isOpen && (
                <div className="filter-dropdown-menu" onClick={(e) => e.stopPropagation()}>
                    {options.map(option => (
                        <div
                            key={option}
                            className={`filter-dropdown-item ${value === option ? 'active' : ''}`}
                            onClick={() => {
                                onChange(option);
                                setIsOpen(false);
                            }}
                        >
                            {option}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Map header with title on first row, filters on second row
 * Search & theme toggle are rendered separately inside the map
 */

// Helper to fetch sites for all states in a country in parallel
const fetchAllSitesForCountry = async (countryId, states) => {
    try {
        const promises = states.map(st => fetchLocationsFilter({ country_id: countryId, state_id: st.state_id }));
        const results = await Promise.all(promises);
        const allSites = results
            .filter(res => res && res.status && Array.isArray(res.data))
            .flatMap(res => res.data);
        return allSites;
    } catch (e) {
        console.error('Error fetching all sites for country:', e);
        return [];
    }
};

/**
 * Map header with title on first row, filters on second row
 * Search & theme toggle are rendered separately inside the map
 */
const MapHeader = ({ onFilterChange, stations = [] }) => {
    // Filter states
    const [country, setCountry] = useState('Select Country [All]');
    const [state, setState] = useState('Select State [All]');
    const [zone, setZone] = useState('Select Sites list [All]');
    const [ward, setWard] = useState('Select Location list [All]');
    const [status, setStatus] = useState('Select Status [All]');

    // Dynamic API data lists
    const [countriesList, setCountriesList] = useState([]);
    const [statesList, setStatesList] = useState([]);
    const [sitesList, setSitesList] = useState([]);
    const [locationsList, setLocationsList] = useState([]);
    const [statusesList, setStatusesList] = useState([]);
    const [sitesForCountry, setSitesForCountry] = useState([]);

    // Derived options for dropdown UI
    const countriesOptions = React.useMemo(() => ['Select Country [All]', ...countriesList.map(c => c.country_name).filter(Boolean)], [countriesList]);
    const statesOptions = React.useMemo(() => ['Select State [All]', ...statesList.map(s => s.state_name).filter(Boolean)], [statesList]);
    const sitesOptions = React.useMemo(() => ['Select Sites list [All]', ...sitesList.map(s => s.site_name).filter(Boolean)], [sitesList]);
    const locationsOptions = React.useMemo(() => ['Select Location list [All]', ...locationsList.map(l => l.location_name).filter(Boolean)], [locationsList]);

    const statusOptions = React.useMemo(() => {
        if (statusesList.length > 0) {
            return ['Select Status [All]', ...statusesList.map(s => s.description).filter(Boolean)];
        }
        const uniqueStatuses = [...new Set(stations.map(s => s.status_text).filter(Boolean))];
        return ['Select Status [All]', ...uniqueStatuses];
    }, [stations, statusesList]);

    // Initial load of countries and status data
    React.useEffect(() => {
        const loadInitialData = async () => {
            const res = await fetchLocationsFilter();
            if (res && res.status) {
                setCountriesList(res.data || []);
                if (res.statusdata) {
                    setStatusesList(res.statusdata);
                }
            }
        };
        loadInitialData();
    }, []);

    // Change handlers
    const handleCountryChange = async (newCountry) => {
        setCountry(newCountry);
        setState('Select State [All]');
        setZone('Select Sites list [All]');
        setWard('Select Location list [All]');
        setStatesList([]);
        setSitesList([]);
        setLocationsList([]);
        setSitesForCountry([]);

        if (newCountry === 'Select Country [All]' || !newCountry) {
            return;
        }

        const selectedCountry = countriesList.find(c => c.country_name === newCountry);
        if (selectedCountry) {
            const res = await fetchLocationsFilter({ country_id: selectedCountry.country_id });
            if (res && res.status) {
                const states = res.data || [];
                setStatesList(states);

                // Fetch all sites for the country in parallel to determine allowedSites
                const allCountrySites = await fetchAllSitesForCountry(selectedCountry.country_id, states);
                setSitesForCountry(allCountrySites);
            }
        }
    };

    const handleStateChange = async (newState) => {
        setState(newState);
        setZone('Select Sites list [All]');
        setWard('Select Location list [All]');
        setSitesList([]);
        setLocationsList([]);

        if (newState === 'Select State [All]' || !newState) {
            return;
        }

        const selectedCountry = countriesList.find(c => c.country_name === country);
        const selectedState = statesList.find(s => s.state_name === newState);
        if (selectedCountry && selectedState) {
            const res = await fetchLocationsFilter({
                country_id: selectedCountry.country_id,
                state_id: selectedState.state_id
            });
            if (res && res.status) {
                setSitesList(res.data || []);
            }
        }
    };

    const handleZoneChange = async (newZone) => {
        setZone(newZone);
        setWard('Select Location list [All]');
        setLocationsList([]);

        if (newZone === 'Select Sites list [All]' || !newZone) {
            return;
        }

        const selectedCountry = countriesList.find(c => c.country_name === country);
        const selectedState = statesList.find(s => s.state_name === state);
        const selectedSite = sitesList.find(s => s.site_name === newZone);

        if (selectedCountry && selectedState && selectedSite) {
            const res = await fetchLocationsFilter({
                country_id: selectedCountry.country_id,
                state_id: selectedState.state_id,
                site_id: selectedSite.site_id
            });
            if (res && res.status) {
                setLocationsList(res.data || []);
            }
        }
    };

    // Propagate filters up to parent
    React.useEffect(() => {
        if (onFilterChange) {
            let allowedSites = null;
            if (zone !== 'Select Sites list [All]') {
                allowedSites = [zone];
            } else if (state !== 'Select State [All]') {
                allowedSites = sitesList.map(s => s.site_name).filter(Boolean);
            } else if (country !== 'Select Country [All]') {
                allowedSites = sitesForCountry.map(s => s.site_name).filter(Boolean);
            }

            onFilterChange({
                country: country === 'Select Country [All]' ? '' : country,
                state: state === 'Select State [All]' ? '' : state,
                site: zone === 'Select Sites list [All]' ? '' : zone,
                location: ward === 'Select Location list [All]' ? '' : ward,
                status: status === 'Select Status [All]' ? '' : status,
                allowedSites
            });
        }
    }, [country, state, zone, ward, status, sitesList, sitesForCountry, onFilterChange]);

    return (
        <div className="map-header">
            {/* Row 1: Title */}
            <div className="map-header-title-row">
                <h1 className="map-header-title">AtmosPOLE Network</h1>
            </div>

            {/* Row 2: Filter Dropdowns */}
            <div className="map-header-filters-row">
                <FilterDropdown
                    label={country}
                    options={countriesOptions}
                    value={country}
                    onChange={handleCountryChange}
                />
                <FilterDropdown
                    label={state}
                    options={statesOptions}
                    value={state}
                    onChange={handleStateChange}
                />
                <FilterDropdown
                    label={zone}
                    options={sitesOptions}
                    value={zone}
                    onChange={handleZoneChange}
                />
                <FilterDropdown
                    label={ward}
                    options={locationsOptions}
                    value={ward}
                    onChange={setWard}
                />
                <FilterDropdown
                    label={status}
                    options={statusOptions}
                    value={status}
                    onChange={setStatus}
                />
                {/* <FilterDropdown
                    label={aqiRange}
                    options={['AQI Range', '0-50', '51-150', '151-300', '301+']}
                    value={aqiRange}
                    onChange={setAqiRange}
                /> */}
            </div>
        </div>
    );
};

/**
 * Map controls (search + theme toggle) - rendered inside the map wrapper
 */
export const MapControls = () => {
    const { isDark, toggleTheme } = useTheme();

    return (
        <div className="map-controls-overlay">
            <button className="map-control-btn" title="Search">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8" />
                    <path d="M21 21l-4.35-4.35" />
                </svg>
            </button>

            <button
                className="map-control-btn"
                onClick={toggleTheme}
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
            >
                {isDark ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="5" />
                        <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
                    </svg>
                ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                    </svg>
                )}
            </button>
        </div>
    );
};

export default MapHeader;
